import { MeterPlacement } from '@enmon/commons/lib/enmon';
import type { Commodity } from '@enmon/commons/lib/shared';
import { InventoryMeterMountType, type UpsertInventoryMeterDto } from '@enmon/commons/lib/tools/api/inventory-meter';
import type { OutputTechnology } from '@enmon/commons/lib/tools/types';
import { readMeterPlacementSelection } from '@/features/inventorization/domain/inventorization-identity';
import { readInventoryMeterMountType } from '@/features/inventorization/domain/inventorization-technical';
import { normalizeSubmitDetail, toSubmitFiniteNumber } from '@/features/sync/engine/inventorization-submit-detail';
import type { InventorizationSyncPayload } from '@/features/sync/engine/inventorization-sync-payload';
import { readRegisterObis, readRegisterTariff, readRegisterUnit } from '@/shared/meters/register-metadata';

type InventorizationSubmitDataOverrides = {
  _id?: string;
  accessibility: string[] | null;
  apiKey?: string | null;
  buildingFloor: number | null;
  calibrated: number | null;
  commodity: Commodity | null;
  detail: Record<string, unknown>;
  inventory_location_building: number | string | null;
  lat: number | null;
  lng: number | null;
  manufacturer?: number;
  manufactured: number | null;
  name: string | null;
  owner: string | null;
  ownerType: string | null;
  outputs?: Array<{
    id?: number | string;
    is_occupied: boolean;
    remote_reading_owner?: string | null;
    technology: OutputTechnology;
  }>;
  placement: MeterPlacement;
  registers?: Array<Record<string, unknown>>;
  serial: string | null;
  submitted_by: string | null;
};

export type InventorizationSubmitData = Omit<
  Partial<UpsertInventoryMeterDto>,
  keyof InventorizationSubmitDataOverrides
> &
  InventorizationSubmitDataOverrides;

export function mapInventorizationSyncPayloadToSubmitData(
  payload: InventorizationSyncPayload,
  input: { submittedAtFallback?: string } = {},
): InventorizationSubmitData {
  const detail = normalizeSubmitDetail(payload.technical.detail);
  const mountType = readInventoryMeterMountType(payload.technical.mountType);
  const placement = readMeterPlacementSelection(payload.identity.placement) || MeterPlacement.Main;
  const isRemoteReadingMounted = mountType !== InventoryMeterMountType.Free;
  const isNewMeterSuggestion = payload.identity.newMeter;
  const submitData: InventorizationSubmitData = {
    accessibility: payload.technical.accessibility,
    apiKey: nullIfEmpty(payload.identity.apiKey),
    buildingFloor: normalizeBuildingFloor(payload.position.floor),
    calibrated: readIntegerOrNull(payload.identity.calibrated),
    commodity: payload.identity.commodity || null,
    detail,
    floorplan_lat: payload.position.floorplanLat ?? 0,
    floorplan_lng: payload.position.floorplanLng ?? 0,
    internal_note: nullIfEmpty(payload.technical.note),
    internal_supply_point_code: nullIfEmpty(payload.technical.detail.internalSupplyPointCode),
    inventory_location_building: readBuildingId(payload.position.buildingId),
    lat: payload.position.gpsLat,
    lng: payload.position.gpsLng,
    lorawan_rssi: readNegativeDbm(payload.technical.detail.lorawanRssi),
    manufactured: readIntegerOrNull(payload.identity.manufactured),
    meter_mount_technology: isRemoteReadingMounted ? payload.technical.mountTechnology || null : null,
    meter_mount_type: mountType,
    name: nullIfEmpty(payload.technical.monitoredEntity) ?? nullIfEmpty(payload.identity.serial),
    nbiot_rssi: readNegativeDbm(payload.technical.detail.nbiotRssi),
    new_meter: isNewMeterSuggestion,
    owner: nullIfEmpty(payload.context.ownerName),
    ownerType: nullIfEmpty(payload.technical.detail.ownerType),
    outputs: (payload.technical.outputs ?? []).map(output => ({
      ...(typeof output.id !== 'undefined' ? { id: output.id } : {}),
      is_occupied: output.isOccupied,
      remote_reading_owner: output.isOccupied ? nullIfEmpty(output.remoteReadingOwner) : null,
      technology: output.technology,
    })),
    placement,
    registers: payload.readings.registers.map(mapRegisterToSubmitData),
    remote_reading_owner: isRemoteReadingMounted ? nullIfEmpty(payload.technical.remoteReadingOwner) : null,
    remote_reading_serial: isRemoteReadingMounted ? nullIfEmpty(payload.technical.remoteReadingSerial) : null,
    requires_manual_aks: toBoolean(payload.technical.detail.requiresManualAks) ?? false,
    serial: nullIfEmpty(payload.identity.serial),
    submitted_at: payload.submittedAt ?? input.submittedAtFallback ?? new Date().toISOString(),
    submitted_by: payload.submittedBy ?? null,
    tenant: nullIfEmpty(payload.context.tenantName),
  };

  if (payload._id) {
    submitData._id = payload._id;
  }

  if (payload.id) {
    submitData.id = payload.id;
  }

  const manufacturer = toSubmitFiniteNumber(payload.identity.manufacturer);
  if (manufacturer !== null) {
    submitData.manufacturer = manufacturer;
  }

  const meterModel = toSubmitFiniteNumber(payload.identity.meterModel);
  if (meterModel !== null) {
    submitData.meter_model = meterModel;
  }

  return submitData;
}

function mapRegisterToSubmitData(register: InventorizationSyncPayload['readings']['registers'][number]) {
  const name = nullIfEmpty(register.label);

  return {
    ...(name ? { name } : {}),
    obis: readRegisterObis(register.obis),
    reading_counter: toSubmitFiniteNumber(register.counter),
    tariff: readRegisterTariff(register.tariff),
    unit: readRegisterUnit(register.unit),
  };
}

function readBuildingId(value: number | string | null | undefined) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const text = nullIfEmpty(value);

  if (!text) {
    return null;
  }

  return toSubmitFiniteNumber(text) ?? text;
}

function normalizeBuildingFloor(value: unknown) {
  const text = nullIfEmpty(value);

  if (!text) {
    return null;
  }

  return /^-?\d+$/.test(text) ? Number(text) : null;
}

function readIntegerOrNull(value: unknown) {
  const number = toSubmitFiniteNumber(value);

  return number === null ? null : Math.trunc(number);
}

function readNegativeDbm(value: unknown) {
  const number = toSubmitFiniteNumber(value);

  return number === null ? null : -Math.abs(number);
}

function nullIfEmpty(value: unknown) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';

  return text || null;
}

function toBoolean(value: unknown) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return null;
}
